import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchContents,
  addContent,
  updateContent,
  deleteContent,
  clearContentError,
  fetchContentById,
} from "../features/content/contentSlice";

import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  useTheme,
  Paper,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import ContentForm from "../components/content/ContentForm";
import ContentList from "../components/content/ContentList";
import RichEditorDialog from "../components/content/RichEditorDialog";
import CallbackRequests from "../components/admin/CallbackRequests";
import SubjectUserAccess from "../components/admin/SubjectUserAccess";
import QuizAdminPanel from "../components/admin/QuizAdminPanel";
import BulkHtmlImport from "../components/admin/BulkHtmlImport";
import FilterListIcon from "@mui/icons-material/FilterList";




function TextEditor() {
  const dispatch = useDispatch();
  // Use adminContents if available, otherwise fall back to contents
  const { contents: allContents, adminContents, loading, error } = useSelector((state) => state.content);
  const contents = adminContents.length > 0 ? adminContents : allContents;
  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
    accent: "#A380FF",
  };

  const [editing, setEditing] = useState(null);
  const { register, handleSubmit, reset, setValue } = useForm();
  const [contentValue, setContentValue] = useState("");
  const [openRichEditor, setOpenRichEditor] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState("All");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch all contents on load (without subject filter to get all)
  useEffect(() => {
    // Fetch all contents by not passing a subject
    dispatch(fetchContents(null));
  }, [dispatch]);

  // Refetch when subject filter changes
  useEffect(() => {
    if (selectedSubject === "All") {
      dispatch(fetchContents(null));
    } else {
      dispatch(fetchContents(selectedSubject));
    }
  }, [selectedSubject, dispatch]);

  // Handle backend errors
  useEffect(() => {
    if (error) {
      // Extract error message from error object
      let errorMessage = "An error occurred";
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error?.detail) {
        // FastAPI error format: { detail: "message" } or { detail: [{ msg: "message" }] }
        if (Array.isArray(error.detail)) {
          errorMessage = error.detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(", ");
        } else {
          errorMessage = error.detail;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.error) {
        errorMessage = error.error;
      } else {
        errorMessage = JSON.stringify(error);
      }
      alert(`Error: ${errorMessage}`);
      dispatch(clearContentError());
    }
  }, [error, dispatch]);

  // Filter contents by subject
  const filteredContents = useMemo(() => {
    if (selectedSubject === "All") {
      return contents;
    }
    return contents.filter((c) => c.subject?.toLowerCase() === selectedSubject.toLowerCase());
  }, [contents, selectedSubject]);

  // Get unique subjects from contents
  const availableSubjects = useMemo(() => {
    const subjects = new Set(contents.map((c) => c.subject).filter(Boolean));
    return ["All", ...Array.from(subjects).sort()];
  }, [contents]);

  // Paginated contents
  const paginatedContents = useMemo(() => {
    const start = page * pageSize;
    const end = start + pageSize;
    return filteredContents.slice(start, end);
  }, [filteredContents, page, pageSize]);

  // Fetch full content_text for items in the list (for admin view)
  // This ensures content_text is available for preview and editing
  const contentIdsString = useMemo(() =>
    paginatedContents.map(c => c.id).join(','),
    [paginatedContents]
  );

  useEffect(() => {
    paginatedContents.forEach((content) => {
      if (content.id && !content.content_text) {
        // Fetch full content with content_text for admin preview
        dispatch(fetchContentById(content.id)).catch((err) => {
          console.error(`Failed to fetch content ${content.id}:`, err);
        });
      }
    });
  }, [contentIdsString, paginatedContents, dispatch]);

  // Submit: Add or Update
  const onSubmit = (data) => {
    if (!user) {
      alert("Not logged in");
      return;
    }

    const payload = {
      ...data,
      content_text: contentValue,
      creator_id: user.id,
    };

    if (editing) {
      dispatch(updateContent({ id: editing.id, data: payload })).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          cancelEdit();
          // Refetch contents after update
          if (selectedSubject === "All") {
            dispatch(fetchContents(null));
          } else {
            dispatch(fetchContents(selectedSubject));
          }
        } else if (res.meta.requestStatus === "rejected") {
          // Handle rejection - error will be handled by useEffect above
          const errorPayload = res.payload;
          let errorMessage = "Failed to update content";
          if (errorPayload?.detail) {
            if (Array.isArray(errorPayload.detail)) {
              errorMessage = errorPayload.detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(", ");
            } else {
              errorMessage = errorPayload.detail;
            }
          } else if (errorPayload?.message) {
            errorMessage = errorPayload.message;
          }
          alert(`Update failed: ${errorMessage}`);
        }
      });
    } else {
      dispatch(addContent(payload)).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          reset();
          setContentValue("");
          // Refetch contents after add
          if (selectedSubject === "All") {
            dispatch(fetchContents(null));
          } else {
            dispatch(fetchContents(selectedSubject));
          }
        } else if (res.meta.requestStatus === "rejected") {
          // Handle rejection - error will be handled by useEffect above
          const errorPayload = res.payload;
          let errorMessage = "Failed to add content";
          if (errorPayload?.detail) {
            if (Array.isArray(errorPayload.detail)) {
              errorMessage = errorPayload.detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(", ");
            } else {
              errorMessage = errorPayload.detail;
            }
          } else if (errorPayload?.message) {
            errorMessage = errorPayload.message;
          }
          alert(`Add failed: ${errorMessage}`);
        }
      });
    }
  };

  // Start editing selected content
  const startEdit = async (item) => {
    setEditing(item);
    setValue("title", item.title);
    setValue("video_link", item.video_link || "");
    setValue("content_id", item.content_id);
    setValue("subject", item.subject);

    // If content_text is not available, fetch it
    if (item.id && !item.content_text) {
      try {
        const result = await dispatch(fetchContentById(item.id));
        if (result.meta.requestStatus === "fulfilled") {
          setContentValue(result.payload.content_text || "");
        } else {
          setContentValue(item.content_text || "");
        }
      } catch (err) {
        setContentValue(item.content_text || "");
      }
    } else {
      setContentValue(item.content_text || "");
    }
  };

  // Delete one item
  const deleteOneContent = (id) => {
    if (window.confirm("Are you sure you want to delete this content?")) {
      dispatch(deleteContent(id)).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          // Refetch contents after delete
          if (selectedSubject === "All") {
            dispatch(fetchContents(null));
          } else {
            dispatch(fetchContents(selectedSubject));
          }
        }
      });
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditing(null);
    reset();
    setContentValue("");
  };


  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        minHeight: "100vh",
        background: isDark
          ? "linear-gradient(135deg, #0a0a0a 0%, #1a0a1a 100%)"
          : "linear-gradient(135deg, #fafafa 0%, #ffffff 100%)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: "16px",
          background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            fontWeight={900}
            sx={{
              mb: 1,
              background: `linear-gradient(135deg, #e50914, #6a0dad)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Admin Content Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and edit course content by subject
          </Typography>
        </Box>

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              "& .MuiTab-root": { fontWeight: 700, fontSize: '1rem' },
              "& .Mui-selected": { color: colors.primary }
            }}
          >
            <Tab label="Content Management" />
            <Tab label="Callback Requests" />
            <Tab label="Subject & User Access" />
            <Tab label="Quiz Management" />
            <Tab label="Bulk HTML Import" />
          </Tabs>
        </Box>

        {activeTab === 0 ? (
          <>
            {/* Subject Filter */}
            <Box sx={{ mb: 4 }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel id="subject-filter-label">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FilterListIcon fontSize="small" />
                      <span>Filter by Subject</span>
                    </Stack>
                  </InputLabel>
                  <Select
                    labelId="subject-filter-label"
                    value={selectedSubject}
                    onChange={(e) => {
                      setSelectedSubject(e.target.value);
                      setPage(0); // Reset to first page when filter changes
                    }}
                    label="Filter by Subject"
                    sx={{
                      borderRadius: "12px",
                    }}
                  >
                    {availableSubjects.map((subject) => (
                      <MenuItem key={subject} value={subject}>
                        {subject}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Chip
                  label={`${filteredContents.length} ${filteredContents.length === 1 ? "item" : "items"}`}
                  color="primary"
                  variant="outlined"
                />
              </Stack>
            </Box>

            {/* Form Component */}
            <Box sx={{ mb: 4 }}>
              <ContentForm
                onSubmit={onSubmit}
                onCancel={cancelEdit}
                editing={editing}
                loading={loading}
                register={register}
                handleSubmit={handleSubmit}
                contentValue={contentValue}
                setContentValue={setContentValue}
                setOpenRichEditor={setOpenRichEditor}
              />
            </Box>

            {/* Content List with Pagination */}
            <ContentList
              contents={paginatedContents}
              allContents={filteredContents}
              onEdit={startEdit}
              onDelete={deleteOneContent}
              loading={loading}
              page={page}
              pageSize={pageSize}
              totalItems={filteredContents.length}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />

            {/* Rich Editor Dialog */}
            <RichEditorDialog
              open={openRichEditor}
              onClose={() => setOpenRichEditor(false)}
              value={contentValue}
              onChange={setContentValue}
            />
          </>
        ) : activeTab === 1 ? (
          <CallbackRequests />
        ) : activeTab === 2 ? (
          <SubjectUserAccess />
        ) : activeTab === 3 ? (
          <QuizAdminPanel />
        ) : (
          <BulkHtmlImport />
        )}
      </Paper>
    </Box>
  );
}

export default TextEditor;
